import {EScene} from '@/types/scene.types'
import {Loader} from '@react-three/drei'
import {Canvas} from '@react-three/fiber'
import SettingsOverlay from '@repo/shared/SettingsOverlay/SettingsOverlay'
import {useTracking} from '@repo/shared/Tracking/Tracking.hooks'
import clsx from 'clsx'
import {useAtomValue} from 'jotai'
import {Suspense, useEffect, useRef} from 'react'
import {CountdownScreen} from '../Countdown/Countdown.screen'
import {ErrorScene} from '../ErrorScene/ErrorScene'
import Game from '../Game/Game'
import {GameData} from '../GameData/GameData'
import {Hud} from '../Hud/Hud'
import {InitProvider} from '../InitProvider/InitProvider'
import LobbyScreen from '../Lobby/Lobby.screen'
import {MinimapScreen} from '../Minimap/Minimap.screen'
import {PreLobbyScreen} from '../PreLobby/PreLobby.screen'
import ScoreboardScreen from '../Scoreboard/Scoreboard.screen'
import {SinglePlayerMode} from '../SinglePlayerMode/SinglePlayerMode'
import {gameModeState, sceneState} from '../Store/atoms'
import FrameStore from '../Store/FrameStore'

const fStore = new FrameStore()

export const App = () => {
  const scene = useAtomValue(sceneState)
  const gameMode = useAtomValue(gameModeState)

  const canvasRef = useRef<HTMLCanvasElement>(null)

  const {trackError} = useTracking()

  useEffect(() => {
    // https://discourse.threejs.org/t/context-lost-when-backgrounding-safari-on-ios-17-developer-beta-8/55772
    const contextLostHandler = () => {
      trackError(new Error('WebGL context lost'))
      window.location.reload()
    }

    if (canvasRef.current) {
      canvasRef.current.addEventListener('webglcontextlost', contextLostHandler)
    }

    return () => {
      if (canvasRef.current) {
        canvasRef.current.removeEventListener('webglcontextlost', contextLostHandler)
      }
    }
  }, [])

  if (scene === EScene.ERROR) {
    return <ErrorScene />
  }

  return (
    <InitProvider>
      <Canvas
        ref={canvasRef}
        className={clsx(scene === EScene.PRE_LOBBY && 'opacity-0')}
        // shadows
        camera={{
          fov: 45,
          near: 0.1,
          far: 2000,
          filmGauge: 36,
          // aspect: 16 / 9,
          position: fStore.getInitialCameraPos(),
          rotation: [0, 0, 0],
        }}
      >
        <Suspense fallback={null}>
          <Game />
          {gameMode !== 'single_player' && <GameData />}
        </Suspense>
      </Canvas>
      <PreLobbyScreen />
      <LobbyScreen />
      <Loader
        initialState={active => true} // Initial black out state
        dataInterpolation={p => `Laster ${p.toFixed(0)}%`} // Text
        containerStyles={{background: 'transparent', alignItems: 'flex-start', paddingTop: 40}}
        innerStyles={{
          position: 'fixed',
          top: 40,
          width: '50vw',
          translate: 'transformX(50%)',
          left: '25vw',
          background: 'rgba(255,255,255,0.3)',
        }}
        barStyles={{
          background: 'white',
        }}
      />
      <Hud />
      <MinimapScreen />
      <CountdownScreen />
      <ScoreboardScreen />
      <SettingsOverlay />
      {gameMode === 'single_player' && <SinglePlayerMode />}
    </InitProvider>
  )
}
